import { useState, useEffect, FormEvent } from 'react';
import { Dialog, DialogContent, Box, Tabs, Tab, TextField, Button, Typography, useTheme, IconButton, useMediaQuery } from '@mui/material';
import { X, LogIn, UserPlus } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '../../types/store';
import AppActions from '../../actions/app-actions';
import UIError from '../ui-error';

// TabPanel component to handle tab content
function TabPanel(props:any) {
    const { children, value, index, ...other } = props;

    return (
        <div role="tabpanel" hidden={value !== index} id={`auth-tabpanel-${index}`} aria-labelledby={`auth-tab-${index}`} {...other}>
            {value === index && (
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

export default function AuthPopup({ open, onClose } : { open: boolean, onClose: () => void }) {
    const [tabValue, setTabValue] = useState(0);
    const theme = useTheme(); // Access the theme
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const dispatch = useDispatch();
    const auth = useSelector((state:RootState) => state.auth);
    const translate = useSelector((state:RootState) => state.ui.language.auth);
    
    // Login form state
    const [loginEmail, setLoginEmail] = useState('');
    const [loginPassword, setLoginPassword] = useState('');

    // Registration form state
    const [registerEmail, setRegisterEmail] = useState('');
    const [registerPassword, setRegisterPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordError, setPasswordError] = useState(false);

    useEffect(() => {
        if(open) {
            dispatch(AppActions.LoginRegisterReset());
        }
    }, [open]);
    
    const handleTabChange = (_:any, newValue:number) => {
        setTabValue(newValue);
    };

    const handleLoginSubmit = (event:FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        dispatch(AppActions.Login({ email: loginEmail, password: loginPassword }));
    };

    const handleRegisterSubmit = (event:FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (registerPassword !== confirmPassword) {
            setPasswordError(true);
            return;
        }
        dispatch(AppActions.Register({ email: registerEmail, password: registerPassword, password2: confirmPassword }));
    };

    return (
        <>
            <Dialog closeAfterTransition={false} open={open} onClose={onClose} disableRestoreFocus={false} maxWidth="sm" fullWidth>
                <Box sx={{display: 'flex',  justifyContent: 'space-between',  alignItems: 'center',
                    borderBottom: `1px solid ${theme.palette.divider}`, bgcolor: theme.palette.secondary.main, px: 2}}>
                    <Tabs value={tabValue} onChange={handleTabChange} variant={isMobile ? 'scrollable' : 'fullWidth'} orientation={isMobile ? 'vertical' : 'horizontal'}
                        sx={{flexGrow: 1, borderRight: isMobile ? `1px solid ${theme.palette.divider}` : 'none', width: isMobile ? '120px' : 'auto'}}>
                        <Tab label="Login" id="auth-tab-0" />
                        <Tab label="Register" id="auth-tab-1" />
                    </Tabs>
                    <IconButton edge="end" onClick={onClose} aria-label="close"><X /></IconButton>
                </Box>

                <DialogContent sx={{ padding: 0 }}>
                
                    <TabPanel value={tabValue} index={0}>
                        <form onSubmit={handleLoginSubmit}>
                            <Typography variant="h6" gutterBottom>{translate.signIn}</Typography>
                            <TextField margin="normal" required fullWidth id="login-email" label={translate.email} name="email" autoComplete="email" autoFocus value={loginEmail} 
                                onChange={(e) => setLoginEmail(e.target.value)}
                            />
                            <TextField margin="normal" required fullWidth name="password" label={translate.password} type="password" id="login-password" autoComplete="current-password" value={loginPassword}
                                onChange={(e) => setLoginPassword(e.target.value)}
                            />
                            <UIError message={auth.login.error} />

                            <Button startIcon={<LogIn />} type="submit" disabled={auth.login.isLoading} fullWidth variant="contained" sx={{ mt: 3,  mb: 2, bgcolor: theme.palette.primary.main, '&:hover': { bgcolor: theme.palette.primary.dark,}}}>
                                {translate.buttonLogin}
                            </Button>
                        </form>
                    </TabPanel>

                    <TabPanel value={tabValue} index={1}>
                        {auth.register.isRegistered === true ? (
                            <Box sx={{ p: 4, textAlign: 'center' }}>
                                <Typography variant="h6" color="success.main">{translate.registeredSuccessfully}</Typography>
                            </Box>
                        ) : <form onSubmit={handleRegisterSubmit}>
                            <Typography variant="h6" gutterBottom>{translate.register}</Typography>
                            <TextField margin="normal" required fullWidth id="register-email" label={translate.email} name="email" autoComplete="email" value={registerEmail}
                                onChange={(e) => setRegisterEmail(e.target.value)}/>
                            <TextField margin="normal" required fullWidth name="password" label={translate.password} type="password" id="register-password" value={registerPassword}
                                onChange={(e) => {
                                    setRegisterPassword(e.target.value);
                                    if (confirmPassword && e.target.value !== confirmPassword) {
                                        setPasswordError(true);
                                    } else {
                                        setPasswordError(false);
                                    }
                                }}
                            />
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                name="confirm-password"
                                label={translate.password}
                                type="password"
                                id="confirm-password"
                                error={passwordError}
                                helperText={passwordError ? translate.passwordsDontMatch : ""}
                                value={confirmPassword}
                                onChange={(e) => {
                                    setConfirmPassword(e.target.value);
                                    if (registerPassword !== e.target.value) {
                                        setPasswordError(true);
                                    } else {
                                        setPasswordError(false);
                                    }
                                }}
                            />
                            <UIError message={auth.register.error} />
                            <Button startIcon={<UserPlus />} type="submit" disabled={auth.register.isLoading} fullWidth variant="contained" sx={{mt: 3,  mb: 2, bgcolor: theme.palette.primary.main, '&:hover': { bgcolor: theme.palette.primary.dark }}}>
                                {translate.buttonRegister}
                            </Button>
                        </form>}
                    </TabPanel>
                </DialogContent>
            </Dialog>
        </>
    );
}